import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from '@store/types';
import { AirflowDashboardHandler } from '@api/airflow-dashboard-handler';
import { errorAlert } from '@components/toastify/notify-toast';
import { AIRFLOW_BASE_PATH, TASKS_TAB_PAGE_SIZE } from '../../constants';
import { AirflowTaskDefinition } from '../../types';

const MAPPED_FILTER_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'mapped', label: 'Mapped' },
    { value: 'not-mapped', label: 'Not mapped' },
];

/*
 * Manages the DAG Tasks tab: fetches every task definition once, then filters/paginates
 * entirely client-side (there's no server-side filtering for task definitions)
 */
export function useTasksTab(dagId: string, refreshKey: number) {
    const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);
    const history = useHistory();

    const [allTasks, setAllTasks] = useState<AirflowTaskDefinition[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [operatorFilter, setOperatorFilter] = useState('');
    const [triggerRuleFilter, setTriggerRuleFilter] = useState('');
    const [retriesFilter, setRetriesFilter] = useState('');
    const [mappedFilter, setMappedFilter] = useState('');
    const [page, setPage] = useState(1);

    useEffect(() => {
        setIsLoading(true);
        AirflowDashboardHandler.listDagTasks(Env, dagId, { orderBy: 'task_id' })
            .then((response) => setAllTasks(response.tasks))
            .catch(() => errorAlert('Failed to load tasks.'))
            .finally(() => setIsLoading(false));
    }, [Env, dagId, refreshKey]);

    const operatorOptions = useMemo(() => Array.from(new Set(allTasks.map((t) => t.operator_name ?? t.class_ref?.class_name).filter(Boolean))) as string[], [allTasks]);
    const triggerRuleOptions = useMemo(() => Array.from(new Set(allTasks.map((t) => t.trigger_rule).filter(Boolean))) as string[], [allTasks]);
    const retriesOptions = useMemo(() => Array.from(new Set(allTasks.map((t) => t.retries).filter((r) => r !== undefined))).sort((a, b) => (a ?? 0) - (b ?? 0)), [allTasks]);

    const filteredTasks = useMemo(() => {
        return allTasks.filter((task) => {
            if (search && !task.task_id.toLowerCase().includes(search.toLowerCase())) return false;
            if (operatorFilter && (task.operator_name ?? task.class_ref?.class_name) !== operatorFilter) return false;
            if (triggerRuleFilter && task.trigger_rule !== triggerRuleFilter) return false;
            if (retriesFilter && String(task.retries) !== retriesFilter) return false;
            if (mappedFilter === 'mapped' && !task.is_mapped) return false;
            if (mappedFilter === 'not-mapped' && task.is_mapped) return false;
            return true;
        });
    }, [allTasks, search, operatorFilter, triggerRuleFilter, retriesFilter, mappedFilter]);

    const totalPages = Math.max(1, Math.ceil(filteredTasks.length / TASKS_TAB_PAGE_SIZE));
    const pagedTasks = useMemo(() => filteredTasks.slice((page - 1) * TASKS_TAB_PAGE_SIZE, page * TASKS_TAB_PAGE_SIZE), [filteredTasks, page]);

    const hasActiveFilters = Boolean(search || operatorFilter || triggerRuleFilter || retriesFilter || mappedFilter);

    /*
     * Clears every filter and resets to the first page
     */
    const handleClearFilters = useCallback(() => {
        setSearch('');
        setOperatorFilter('');
        setTriggerRuleFilter('');
        setRetriesFilter('');
        setMappedFilter('');
        setPage(1);
    }, []);

    /*
     * Navigates to a task's detail page
     */
    const handleTaskClick = useCallback(
        (task: AirflowTaskDefinition) => {
            history.push(`${AIRFLOW_BASE_PATH}/${encodeURIComponent(dagId)}/tasks/${encodeURIComponent(task.task_id)}`);
        },
        [history, dagId]
    );

    return {
        isLoading,
        search,
        setSearch: (value: string) => {
            setSearch(value);
            setPage(1);
        },
        operatorFilter,
        setOperatorFilter: (value: string) => {
            setOperatorFilter(value);
            setPage(1);
        },
        triggerRuleFilter,
        setTriggerRuleFilter: (value: string) => {
            setTriggerRuleFilter(value);
            setPage(1);
        },
        retriesFilter,
        setRetriesFilter: (value: string) => {
            setRetriesFilter(value);
            setPage(1);
        },
        mappedFilter,
        setMappedFilter: (value: string) => {
            setMappedFilter(value);
            setPage(1);
        },
        operatorOptions,
        triggerRuleOptions,
        retriesOptions,
        mappedFilterOptions: MAPPED_FILTER_OPTIONS,
        hasActiveFilters,
        handleClearFilters,
        pagedTasks,
        filteredCount: filteredTasks.length,
        totalTasks: allTasks.length,
        page,
        setPage,
        totalPages,
        handleTaskClick,
    };
}
